import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, skip, tap, withLatestFrom } from 'rxjs/operators';

import { isEqual } from 'lodash-es';

import { fieldKeyToDefaultSortOptionKey, SortInfo } from '@stylehaven/components';

import { Column } from '../entities/column.entity';
import { Comparable } from '../entities/misc/comparable.interface';
import { Trackable } from '../entities/misc/trackable.interface';

/**
 * A sort the incoming column set cannot show falls back to the page's default order — you sorted *Alles in
 * archief* by `Vernietigingsdatum`, then hid that column. The table header is the only sort control on a grid
 * table, so without this the ordering becomes one the user can neither see nor change.
 *
 * Compared in the key space the header **emits**, not the one the column declares: it rewrites six dotted keys
 * on the way out, so a column with a dotted `sortField` would otherwise fail to recognise its own sort and
 * lose it on every change.
 *
 * Two things make "the first emission" mean the first *settled* one, and both are load-bearing:
 * `debounceTime(0)` because startup arrives in pieces — each level's saved selection is pushed in separately,
 * so one set can land a tick after another and read as a swap — and the empty-set filter because a host builds
 * this stream before it configures its columns, so the literal first value is the empty set it starts on.
 *
 * Column filters are deliberately *not* touched. A sort is presentation whose only control the change removes;
 * a filter is intent that keeps a visible control, in the active-filter bar, and dropping it would change the
 * result set out from under the user.
 *
 * It takes a **reset callback, not an overview id**: no Taken page persists its sort, so what "reset" means
 * differs per host and only the host knows it.
 *
 * @param chosenSort$ the sort the user *chose*, this session or a previous one — never the page's own default,
 *     which there is nothing to fall back to.
 * @returns the stream that performs the reset. Subscribe to arm it.
 */
export function resetOrphanedSort<T extends Comparable & Trackable>(
    tableColumns$: Observable<Column<T>[]>,
    chosenSort$: Observable<SortInfo | null>,
    reset: () => void
): Observable<void> {
    return tableColumns$.pipe(
        filter(columns => columns.length > 0),
        debounceTime(0),
        distinctUntilChanged(isEqual),
        skip(1),
        withLatestFrom(chosenSort$),
        filter(([columns, sort]) => {
            const canShow: (column: Column<T>) => boolean = column =>
                fieldKeyToDefaultSortOptionKey(column.columnDefinition.sortField) === sort?.field;

            return !!sort?.field && !columns.some(canShow);
        }),
        map(() => undefined),
        tap(() => reset())
    );
}
